/**
 * SceneSpec v1, the resolved spec, the board diff protocol and the watermark
 * (architecture §6.2, §6.3, §6.5).
 *
 * The model never writes a SceneSpec and never receives one — it gets the ~150-token
 * ASCII digest and per-turn delta lines. `start_combat` resolves into this; the client
 * receives the `ResolvedSceneSpec` once and then only diffs, applied strictly in `seq`
 * order and released on the watermark.
 */
import { z } from 'zod';
import { Cell, Cover, Lighting, Ref, TimeOfDay, Weather, } from './primitives.js';
/* --------------------------------------------------------------- placement */
export const Placement = z.enum([
    'near_entrance',
    'ambush',
    'around_player',
    'far_side',
    'at_slot',
]);
export const PropSource = z.enum(['template', 'presence', 'gm_patch']);
export const PropState = z.enum([
    'default',
    'flipped',
    'broken',
    'open',
    'closed',
    'lit',
    'extinguished',
]);
export const PropInteraction = z.enum([
    'flip',
    'climb',
    'push',
    'open',
    'close',
    'light',
    'extinguish',
    'break',
]);
export const ExitKind = z.enum(['door', 'gate', 'stairs', 'open', 'window']);
export const ExitState = z.enum(['open', 'closed', 'locked']);
export const Team = z.enum(['party', 'hostile', 'neutral', 'ally']);
export const TokenSize = z.enum(['tiny', 'small', 'medium', 'large']);
export const Disposition = z.enum(['hostile', 'defensive', 'fleeing', 'bystander']);
/** Enemy intent, set once at `start_combat`; there is no per-round intent tool (§3.3). */
export const EnemyIntent = z.enum([
    'hold_chokepoint',
    'focus_weakest',
    'flank',
    'guard_npc',
    'flee_when_bloodied',
]);
export const SceneSetting = z.enum(['indoor', 'outdoor', 'underground']);
/* -------------------------------------------------------------- SceneSpec */
export const TerrainRect = z.object({
    x: z.number().int(),
    y: z.number().int(),
    w: z.number().int().min(1),
    h: z.number().int().min(1),
    /** 'wall', 'water', 'mud', 'rubble', 'counter' … */
    t: z.string(),
    difficult: z.boolean().default(false),
    blocksMove: z.boolean().default(false),
    blocksSight: z.boolean().default(false),
});
export const PropHazard = z.object({
    type: z.string(),
    dmg: z.string(),
    save: z.enum(['str', 'dex', 'con', 'int', 'wis', 'cha']),
    dc: z.number().int(),
});
/** A prop sits at a cell, or near a named anchor within Chebyshev distance. */
export const PropAnchor = z.object({
    near: z.string(),
    maxDist: z.number().int().min(1).max(2).default(2),
});
export const SceneProp = z.object({
    id: z.string(),
    /** 'table', 'barrel', 'brazier', 'wagon', 'crate' … (~40 kinds in v1) */
    kind: z.string(),
    at: z.union([Cell, PropAnchor]),
    size: z.number().int().min(1).max(3).default(1),
    blocksMove: z.boolean().default(true),
    blocksSight: z.boolean().default(false),
    cover: Cover.default('none'),
    hazard: PropHazard.optional(),
    /** Breakable props only. */
    hp: z.number().int().optional(),
    state: PropState.default('default'),
    interactions: z.array(PropInteraction).default([]),
    source: PropSource,
});
export const SceneExit = z.object({
    id: z.string(),
    at: Cell,
    kind: ExitKind,
    state: ExitState.default('open'),
    /** World graph location ref. */
    leadsTo: z.string(),
});
export const TokenPlacement = z.object({
    placement: Placement,
    slot: z.string().optional(),
});
export const SceneToken = z.object({
    id: z.string(),
    ref: Ref,
    /** Stat block override for an `npc:` token. */
    stat: z.string().optional(),
    team: Team,
    at: z.union([Cell, TokenPlacement]),
    size: TokenSize.default('medium'),
    disposition: Disposition.optional(),
    intent: EnemyIntent.optional(),
    /** Token id this one guards, for `guard_npc`. */
    guards: z.string().optional(),
    hidden: z.boolean().default(false),
    /** Presence activity id → slot placement ('serving', 'drinking'). */
    activity: z.string().optional(),
});
export const SceneObjective = z.object({
    id: z.string(),
    text: z.string().max(80),
    /** Predicates: 'all_hostiles_defeated_or_fled', 'pc_at:exit:door_east', 'any_neutral_dead'. */
    win: z.string().optional(),
    fail: z.string().optional(),
    soft: z.boolean().default(false),
});
export const SceneReveal = z.enum(['slide_from_narration', 'cut']);
export const SceneSpec = z.object({
    v: z.literal(1),
    /** 'scn:<locationId>:<seq>' */
    id: z.string(),
    mapTemplateId: z.string(),
    variant: z.string().optional(),
    grid: z.object({
        type: z.literal('square'),
        cellFt: z.literal(5),
        w: z.number().int().min(8).max(24),
        h: z.number().int().min(6).max(18),
        diagonal: z.enum(['5-5-5', '5-10-5']).default('5-5-5'),
    }),
    env: z.object({
        setting: SceneSetting,
        timeOfDay: TimeOfDay,
        weather: Weather,
        lighting: Lighting,
        /** Shader palette id, e.g. 'warm_tavern', 'wet_stone'. */
        palette: z.string(),
    }),
    terrain: z.object({
        /** 'wood_floor', 'cobble', 'grass', 'dirt' */
        default: z.string(),
        rects: z.array(TerrainRect).max(24),
    }),
    props: z.array(SceneProp).max(20),
    exits: z.array(SceneExit).min(1).max(4),
    tokens: z.array(SceneToken).min(1).max(20),
    objectives: z.array(SceneObjective).min(1).max(3),
    reveal: SceneReveal.default('slide_from_narration'),
    cues: z
        .object({
        musicStem: z.string().optional(),
        sfxOnStart: z.string().optional(),
    })
        .optional(),
});
/**
 * The solver's output. Cell arrays are `w × h` and travel to the client base64-encoded
 * inside the JSON transport, which is why they are typed as strings on the wire and
 * decoded into `Uint8Array`s by the renderer.
 */
export const ResolvedCells = z.object({
    /** base64 of a w×h Uint8Array, terrain index per cell */
    terrain: z.string(),
    blocksMove: z.string(),
    blocksSight: z.string(),
    difficult: z.string(),
    cover: z.string(),
});
export const ResolvedSceneSpec = SceneSpec.extend({
    resolved: z.literal(true),
    cells: ResolvedCells,
    /**
     * In a resolved spec every anchor (`{ near, maxDist }`) has been replaced by the
     * concrete cell the packer chose, so a consumer never has to branch on the union.
     */
    props: z.array(SceneProp.extend({ at: Cell })).max(20),
    /**
     * Likewise every `{ placement }` token has been replaced by the cell the solver
     * placed it on.
     */
    tokens: z.array(SceneToken.extend({ at: Cell })).min(1).max(20),
    /**
     * The same token cells again, keyed by id — a stable channel for a client that
     * wants positions without re-reading the token list (§6.3).
     */
    tokenPositions: z.array(z.object({ id: z.string(), at: Cell })),
    /** The ~150-token ASCII board the model reads (§6.3). */
    digest: z.string(),
});
/* ------------------------------------------------------------ board diffs */
export const HpBand = z.enum(['unhurt', 'hurt', 'bloodied', 'down']);
export const TokenState = z.enum([
    'ok',
    'prone',
    'unconscious',
    'dead',
    'hidden',
    'fled',
]);
export const FxKind = z.enum(['attack', 'miss', 'save', 'heal', 'hazard']);
export const CombatOutcome = z.enum(['victory', 'escape', 'defeat', 'truce', 'fled']);
const diffBase = z.object({
    /** The committed event `seq` that produced this diff; the client applies in order. */
    seq: z.number().int().nonnegative(),
});
export const BoardDiff = z.discriminatedUnion('op', [
    diffBase.extend({
        op: z.literal('scene'),
        spec: ResolvedSceneSpec,
        reveal: SceneReveal,
    }),
    diffBase.extend({
        op: z.literal('move'),
        token: z.string(),
        path: z.array(Cell),
        provoked: z.array(z.string()).default([]),
    }),
    diffBase.extend({
        op: z.literal('hp'),
        token: z.string(),
        delta: z.number().int(),
        /** Numeric for the party; absent for enemies, which show a band instead. */
        hpAfter: z.number().int().optional(),
        band: HpBand,
        crit: z.boolean().optional(),
    }),
    diffBase.extend({
        op: z.literal('badge'),
        token: z.string(),
        condition: z.string(),
        on: z.boolean(),
        source: z.string(),
    }),
    diffBase.extend({
        op: z.literal('fx'),
        kind: FxKind,
        from: z.string().optional(),
        to: z.string().optional(),
        cover: Cover.optional(),
    }),
    diffBase.extend({
        op: z.literal('aoe'),
        shape: z.enum(['circle', 'cone']),
        origin: Cell,
        dir: z.number().optional(),
        cells: z.array(Cell),
        ttlMs: z.number().int().optional(),
        persistentEffectId: z.string().optional(),
    }),
    diffBase.extend({
        op: z.literal('prop_state'),
        prop: z.string(),
        state: PropState,
        coverAfter: Cover,
        blocksSightAfter: z.boolean(),
    }),
    diffBase.extend({
        op: z.literal('token_state'),
        token: z.string(),
        state: TokenState,
    }),
    diffBase.extend({
        op: z.literal('light'),
        lighting: Lighting,
    }),
    diffBase.extend({
        op: z.literal('turn'),
        active: z.string(),
        round: z.number().int().min(1),
    }),
    diffBase.extend({
        op: z.literal('roll'),
        rollId: z.string(),
        phase: z.enum(['requested', 'resolved']),
        /** `RollRequested` / `RollResolved` payload; drives the dice tray (§6.6). */
        payload: z.unknown(),
    }),
    diffBase.extend({
        op: z.literal('end'),
        outcome: CombatOutcome,
    }),
]);
/* -------------------------------------------------------------- watermark */
/**
 * Every sentence handed to pocket-tts carries `w` = the highest committed `seq` at
 * generation time. The voice node reports `sentence_started {w}`; the actor forwards
 * `release {w}` to the client, which applies every held diff `<= w` against its own
 * audio playback clock (§6.5).
 */
export const WatermarkRelease = z.object({
    type: z.literal('release'),
    /** Highest committed `seq` whose diffs may now be shown. */
    w: z.number().int().nonnegative(),
    /** The `say_seq` of the sentence that began playing. */
    saySeq: z.number().int().nonnegative(),
    /** Server timestamp (epoch ms) at which mixing of that sentence began. */
    tStart: z.number(),
});
/** Player-initiated results release immediately; narrator-initiated diffs are held. */
export const DiffRelease = z.enum(['immediate', 'watermark']);
/** A batch of diffs with the release rule the actor decided (§6.5). */
export const BoardDiffBatch = z.object({
    type: z.literal('board_diff'),
    release: DiffRelease,
    /** Present when `release === 'watermark'`: show once `release.w >= holdUntil`. */
    holdUntil: z.number().int().nonnegative().optional(),
    diffs: z.array(BoardDiff).min(1),
});
/** Safety valve: a diff held longer than 8 s is released and logged (§6.5). */
export const WATERMARK_HOLD_TIMEOUT_MS = 8_000;
//# sourceMappingURL=scene-spec.js.map